import { getApolloClient } from '../lib/getApolloClient';
import {
  APPROVE_DAY_SEQUENCES,
  CLOCK_ACTION,
  CREATE_DAY_SEQUENCES,
  DISPUTE_DAY_SEQUENCES,
  GET_DAY_SEQUENCE,
  GET_DAY_SEQUENCES_BY_JOB_IDS,
  GET_DAY_SEQUENCES_FOR_CLIENT,
  GET_DAY_SEQUENCES_FOR_USER,
  GET_DAY_SEQUENCES_REQUIRING_ACTION,
  PROPOSE_DAY_SEQUENCE_CHANGES,
  RESOLVE_DAY_SEQUENCE_DISPUTES,
} from './daySequence.gql';
import {
  BreakEvent,
  ClockGeoRecordInput,
  CreateDaySequenceInput,
  DaySequence,
  DaySequenceChangeInput,
  DaySequenceStatus,
} from '../generated/gql-types';
import { handleGraphQLErrors } from '../util/graphqlError.util';

const getDaySequencesByJobIds = async (
  jobIds: string[],
): Promise<DaySequence[]> => {
  const uniqueJobIds = [
    ...new Set(jobIds.map((jobId) => jobId.trim()).filter(Boolean)),
  ];
  if (uniqueJobIds.length === 0) {
    return [];
  }

  try {
    const { data } = await getApolloClient().query<{
      getDaySequencesByJobIds: DaySequence[];
    }>({
      query: GET_DAY_SEQUENCES_BY_JOB_IDS,
      variables: { jobIds: uniqueJobIds },
    });
    return data.getDaySequencesByJobIds ?? [];
  } catch (error) {
    console.error('Error fetching day sequences by job ids:', error);
    return [];
  }
};

const getDaySequencesByJobId = async (
  jobId: string,
  limit: number,
  lastEvaluatedKey?: string,
): Promise<{
  daySequences: DaySequence[];
  hasMore: boolean;
  lastEvaluatedKey: string | null;
}> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_DAY_SEQUENCE,
      variables: {
        jobId,
        limit,
        ...(lastEvaluatedKey && { lastEvaluatedKey }),
      },
    });
    if (data.getDaySequencesForJob) {
      return {
        daySequences: data.getDaySequencesForJob.daySequences,
        hasMore: data.getDaySequencesForJob.hasMore,
        lastEvaluatedKey: data.getDaySequencesForJob.lastEvaluatedKey,
      };
    }

    return {
      daySequences: [],
      hasMore: false,
      lastEvaluatedKey: null,
    };
  } catch (error) {
    console.error('Error fetching day sequences in action:', error);
    return {
      daySequences: [],
      hasMore: false,
      lastEvaluatedKey: null,
    };
  }
};

const getDaySequencesForClient = async (
  clientId: string,
): Promise<DaySequence[]> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_DAY_SEQUENCES_FOR_CLIENT,
      variables: { clientId },
    });
    return data.getDaySequencesForClient;
  } catch (error) {
    console.error('Error fetching day sequences for client:', error);
    throw error;
  }
};

const getDaySequencesForUser = async (
  userId: string,
  statuses?: DaySequenceStatus[],
): Promise<DaySequence[]> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_DAY_SEQUENCES_FOR_USER,
      variables: { userId, ...(statuses && { statuses }) },
    });
    return data.getDaySequencesForUser;
  } catch (error) {
    console.error('Error fetching day sequences for user:', error);
    throw error;
  }
};

const getDaySequencesRequiringAction = async (
  tenantId: string,
): Promise<DaySequence[]> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_DAY_SEQUENCES_REQUIRING_ACTION,
      variables: { tenantId },
    });
    return data.getDaySequencesRequiringAction;
  } catch (error) {
    console.error('Error fetching day sequences for tenant:', error);
    throw error;
  }
};

export interface ClockActionResult {
  success: boolean;
  daySequence?: DaySequence;
  error?: {
    message: string;
    code: string;
    status: number;
    actionItems?: Array<{
      code: string;
      itemId: string;
      message: string;
    }>;
  };
}

const clockAction = async (
  jobId: string,
  clockInTimestamp?: string,
  clockOutTimestamp?: string,
  breaks?: BreakEvent[],
  clockInGeo?: ClockGeoRecordInput,
  clockOutGeo?: ClockGeoRecordInput,
): Promise<ClockActionResult> => {
  try {
    const { data, errors } = await getApolloClient().mutate({
      mutation: CLOCK_ACTION,
      variables: {
        entry: {
          jobId,
          clockInTimestamp,
          clockOutTimestamp,
          breaks,
          clockInGeo,
          clockOutGeo,
        },
      },
    });
    handleGraphQLErrors(errors);
    if (!data?.clockAction) {
      return {
        success: false,
        error: {
          message: 'Clock action returned no timesheet',
          code: 'NO_DATA',
          status: 500,
        },
      };
    }
    return {
      success: true,
      daySequence: data.clockAction,
    };
  } catch (error: any) {
    console.error('[ClockAction] Error:', error);

    // Parse GraphQL error into structured format
    const graphQLErrors = error?.graphQLErrors || [];
    console.log('[ClockAction] graphQLErrors count:', graphQLErrors.length);

    if (graphQLErrors.length > 0) {
      const gqlError = graphQLErrors[0];
      const message = gqlError.message || 'An error occurred';

      console.log('[ClockAction] gqlError.message:', message);
      console.log(
        '[ClockAction] gqlError.extensions:',
        JSON.stringify(gqlError.extensions),
      );

      // Try to get actionItems from extensions first
      let actionItems = gqlError.extensions?.actionItems;
      let code = gqlError.extensions?.code || 'UNKNOWN';

      // If actionItems not in extensions, try to parse from message
      // Format: "message | details={\"actionItems\":[...]}"
      if (!actionItems && message.includes('details=')) {
        console.log(
          '[ClockAction] Attempting to parse actionItems from message',
        );
        try {
          const detailsMatch = message.match(/details=(\{.*\})$/);
          console.log('[ClockAction] detailsMatch:', detailsMatch);
          if (detailsMatch) {
            const details = JSON.parse(detailsMatch[1]);
            console.log(
              '[ClockAction] Parsed details:',
              JSON.stringify(details),
            );
            actionItems = details.actionItems;
            // Extract the actual business error code from actionItems if available
            if (actionItems?.length > 0) {
              const actionCode = actionItems[0].code;
              // BUS_503_001 -> BUS_503
              if (actionCode?.startsWith('BUS_')) {
                code = actionCode.split('_').slice(0, 2).join('_');
              }
            }
          }
        } catch (parseError) {
          console.error(
            '[ClockAction] Error parsing actionItems from message:',
            parseError,
          );
        }
      }

      // Clean up message by removing the details JSON
      const cleanMessage = message.replace(/ \| details=\{.*\}$/, '');

      const errorResult = {
        success: false,
        error: {
          message: cleanMessage,
          code,
          status: gqlError.extensions?.status || 500,
          actionItems,
        },
      };
      console.log(
        '[ClockAction] Final error result:',
        JSON.stringify(errorResult),
      );
      return errorResult;
    }

    // Fallback for network errors
    return {
      success: false,
      error: {
        message: error?.message || 'An unexpected error occurred',
        code: 'UNKNOWN',
        status: 500,
      },
    };
  }
};

const approveDaySequences = async (ids: string[]): Promise<DaySequence[]> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: APPROVE_DAY_SEQUENCES,
      variables: { ids },
    });
    if (data.approveDaySequences) {
      return data.approveDaySequences;
    }
    return [];
  } catch (error) {
    console.error('Error approving proposed change:', error);
    return [];
  }
};
const disputeDaySequences = async (ids: string[]): Promise<DaySequence[]> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: DISPUTE_DAY_SEQUENCES,
      variables: { ids },
    });
    if (data.disputeDaySequences) {
      return data.disputeDaySequences;
    }
    return [];
  } catch (error) {
    console.error('Error disputing proposed change:', error);
    return [];
  }
};

const proposeDaySequenceChanges = async (
  daySequenceChanges: DaySequenceChangeInput[],
): Promise<DaySequence[]> => {
  const { data, errors } = await getApolloClient().mutate({
    mutation: PROPOSE_DAY_SEQUENCE_CHANGES,
    variables: { daySequenceChanges },
  });
  handleGraphQLErrors(errors);
  if (data?.proposeDaySequenceChanges?.length) {
    return data.proposeDaySequenceChanges;
  }
  if (daySequenceChanges.length > 0) {
    throw new Error('Propose timesheet change returned no data');
  }
  return [];
};

const resolveDaySequenceDisputes = async (
  daySequenceChanges: DaySequenceChangeInput[],
): Promise<DaySequence[]> => {
  const { data, errors } = await getApolloClient().mutate({
    mutation: RESOLVE_DAY_SEQUENCE_DISPUTES,
    variables: { daySequenceChanges },
  });
  handleGraphQLErrors(errors);
  if (data?.resolveDaySequenceDisputes?.length) {
    return data.resolveDaySequenceDisputes;
  }
  if (daySequenceChanges.length > 0) {
    throw new Error('Resolve day sequence disputes returned no data');
  }
  return [];
};

const createDaySequences = async (
  input: CreateDaySequenceInput,
): Promise<DaySequence[]> => {
  try {
    const { data, errors } = await getApolloClient().mutate({
      mutation: CREATE_DAY_SEQUENCES,
      variables: { input },
    });

    handleGraphQLErrors(errors);

    if (data?.createDaySequences) {
      return data.createDaySequences;
    }
    return [];
  } catch (error) {
    console.error('Error creating day sequences:', error);
    throw error;
  }
};

export const daySequenceActions = {
  getDaySequencesByJobId,
  getDaySequencesByJobIds,
  getDaySequencesForClient,
  getDaySequencesForUser,
  getDaySequencesRequiringAction,
  clockAction,
  approveDaySequences,
  disputeDaySequences,
  proposeDaySequenceChanges,
  resolveDaySequenceDisputes,
  createDaySequences,
};
